import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import Toast from "./Toast";
import { IoClose } from "react-icons/io5";
import { useFocusTrap } from "../utils/focusTrap";

// Generated entirely client-side (no network call, no cost) — just draws
// the QR code onto a canvas from the given URL.
export default function QRCodeModal({ value, title, onClose }) {
  const canvasRef = useRef(null);
  const cardRef = useRef(null);
  const [error, setError] = useState(false);
  useFocusTrap(cardRef, true);

  // Every other overlay in the app (FollowListModal, ReportModal,
  // ImageCropModal, ImageLightbox, NotificationBell, OnboardingModal) locks
  // background scroll while open — this one didn't.
  useEffect(() => {
    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (!canvasRef.current || !value) return;
    QRCode.toCanvas(canvasRef.current, value, { width: 220, margin: 1 }, err => {
      if (err) {
        console.log("QR CODE ERROR:", err);
        setError(true);
      }
    });
  }, [value]);

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose?.();
  }

  return createPortal(
    <div style={backdrop} onClick={handleBackdropClick}>
      <div style={card} ref={cardRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title || "Scan to visit"} onClick={e => e.stopPropagation()}>
        <button style={closeBtn} onClick={onClose} aria-label="Close">
          <IoClose />
        </button>

        <h3 style={heading}>{title || "Scan to visit"}</h3>

        <div style={canvasWrap}>
          <canvas ref={canvasRef} />
        </div>

        <p style={urlText}>{value}</p>
      </div>

      {error && <Toast text="Couldn't generate the QR code." onClose={() => setError(false)} />}
    </div>,
    document.body
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(20, 14, 8, 0.6)",
  // Was tied with ImageLightbox's 1300 — an accidental collision resolved
  // only by mount order. This sits just under it in the app's stacking scale.
  zIndex: 1250,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};

const card = {
  position: "relative",
  width: "100%",
  maxWidth: 320,
  background: "var(--bg-surface)",
  borderRadius: 18,
  padding: "28px 24px 22px",
  textAlign: "center",
  boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
};

const closeBtn = {
  position: "absolute",
  top: 12,
  right: 12,
  display: "flex",
  background: "none",
  border: "none",
  fontSize: 20,
  color: "var(--text-muted)",
  cursor: "pointer",
};

const heading = {
  fontFamily: "'Fraunces', serif",
  fontSize: 17,
  color: "var(--text-heading)",
  margin: "0 0 16px",
};

const canvasWrap = {
  display: "inline-flex",
  padding: 12,
  background: "#FFFFFF",
  borderRadius: 12,
};

const urlText = {
  fontSize: 11.5,
  color: "var(--text-muted)",
  marginTop: 14,
  wordBreak: "break-all",
};
